import {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
    type CSSProperties,
    type ReactNode,
} from 'react';

import type {
    CostKey,
    PublicDocument,
    PublicFullReport,
    PublicLanguage,
    PublicReport,
    PublicReportChapter,
} from '@/entities/publicReport';

import { brandingStyle } from '../branding';
import { copyFor, type PublicCopy } from '../copy';
import { jumpTo } from '../jump';
import {
    costBreakdown,
    costTable,
    inReadingOrder,
    nhgRowAnchor,
    reportOutline,
    type IndexedEntry,
} from '../reportIndex';
import { useFullReportState, type FullReportState } from '../useFullReportState';
import type { PublicDocumentState } from '../usePublicReport';
import {
    costRowId,
    outlineRowId,
    reportPanelId,
    reportTabId,
    useReportView,
    type ReportTab,
    type ReportView,
} from '../useReportView';
import { CompanyBar } from './CompanyBar';
import { CostBlocks } from './CostBlocks';
import { CostBreakdownDrawer } from './CostBreakdown';
import { CostTable } from './CostTable';
import { EnergyLabelBlock } from './EnergyLabelBlock';
import { FullReport } from './FullReport';
import { InspectorCard } from './InspectorCard';
import { InvoiceBlock } from './InvoiceBlock';
import { KeyFindings } from './KeyFindings';
import { NhgBlock } from './NhgBlock';
import { NhgTab } from './NhgTab';
import { PartsList } from './PartsList';
import { PublicFooter } from './PublicFooter';
import { ReportHero } from './ReportHero';
import { ReportOutline } from './ReportOutline';
import { ReportSummary } from './ReportSummary';
import { ReportTabs } from './ReportTabs';
import { ShareBlock } from './ShareBlock';
import { TranslationNotice } from './TranslationNotice';

export interface PublicReportBlocksProps {
    report: PublicReport;
    language: PublicLanguage;
    onLanguageChange: (language: PublicLanguage) => void;
    onShare: (email: string) => Promise<void>;
    onAsk: (message: string, email: string | null) => Promise<void>;
    /** `1` op de publieke pagina; `2` in de admin-preview, waar de schermkop al `h1` is. */
    heroLevel: 1 | 2;
    /**
     * De downloadknop haalt de PDF zelf op: met de sessie in de admin-preview, met
     * de link op de publieke pagina. `false`: de browser volgt de link.
     */
    onOpenDocument?: (document: PublicDocument) => boolean | void;
    /** Welke downloadknop nu ophaalt, en van welke de PDF niet kwam. */
    documentState?: PublicDocumentState;
    /** Alleen in de admin-preview: vragen en doorsturen versturen niets, en deze regel zegt dat. */
    previewNote?: string;
    /** Een foto laadt niet meer: haal verse links op. */
    onLinkExpired?: () => void;
    /**
     * Blijven de tabbladen en de leesbalk bovenaan plakken? Op de klantpagina
     * wel; in het kader van kantoor niet, want daar scrollt de pagina niet zelf.
     */
    pinned?: boolean;
    /** De gekozen taal wordt nog vertaald, of dat lukte niet. */
    translationState?: 'pending' | 'failed' | null;
}

type NhgChapter = Extract<PublicReportChapter, { kind: 'nhg' }>;

type GuideChapter = Extract<PublicReportChapter, { kind: 'guide' }>;

/** Een tabblad met een lijst waaruit de koper naar het rapport springt. */
type ListTab = 'overzicht' | 'kosten';

/**
 * Wat een tabblad doet als het net open is gegaan: bovenaan beginnen, terug naar
 * een regel, of niets, omdat het tabblad zelf naar zijn doel springt.
 */
type Arrival = { kind: 'top' } | { kind: 'row'; id: string } | { kind: 'self' };

/**
 * De inhoud van een geladen rapport, van bedrijfsbalk tot voet. Een blok zonder
 * gegevens (geen energielabel, geen factuur, kosten verborgen) valt weg zonder
 * een leeg vak achter te laten.
 *
 * Met het volledige rapport staan onder de bedrijfsbalk vier tabbladen, die
 * blijven staan tijdens het lezen:
 *
 * - **Overzicht**: een rustige samenvatting (adres, datum, inspecteur, de zin die
 *   het rapport samenvat, de drie bedragen en de PDF), dan elk onderdeel met de
 *   koppen eronder en het oordeel in woorden, en dan de inspecteur, delen en de
 *   factuur. Een tik op een onderdeel opent het in het rapport.
 * - **Kosten**: de drie bedragen, met de bevindingen erachter, en per onderdeel
 *   Direct, Op termijn en Verbetering met een totaal. Een tik op een regel opent
 *   het onderdeel in het rapport.
 * - **Rapport**: het volledige geschreven rapport, als rustig document.
 * - **NHG**: de NHG-bijlage, alleen als het rapport die heeft en de kosten
 *   zichtbaar zijn.
 *
 * Welk tabblad open is en waar in het rapport, staat in de adresbalk
 * (`#overzicht`, `#kosten`, `#rapport-6-2`, `#nhg`), zodat elke stand een link
 * is, ook in de kantoorpreview, en Terug en Vooruit werken. Een sprong naar het
 * rapport landt onder de vaste balken en licht het doel even op. Terug naar het
 * overzicht, met de knop of met Terug in de browser, komt uit bij de regel waar
 * de koper vandaan kwam.
 *
 * Zonder leesbaar volledig rapport blijft het overzicht zoals het altijd was,
 * zonder tabbladen.
 */
export function PublicReportBlocks({
    report,
    language,
    onLanguageChange,
    onShare,
    onAsk,
    heroLevel,
    onOpenDocument,
    documentState,
    previewNote,
    onLinkExpired,
    pinned = true,
    translationState = null,
}: PublicReportBlocksProps) {
    const copy = copyFor(language);
    const sectionLevel = heroLevel === 1 ? 2 : 3;
    const view = useReportView();
    const idPrefix = useId();
    /* De leesvolgorde van het hele rapport: bevindingen vooraan, toelichting en voorwaarden achteraan. */
    const fullReport = useMemo(
        () => (report.fullReport === null ? null : inReadingOrder(report.fullReport)),
        [report.fullReport],
    );
    const reading = useFullReportState(fullReport, fullReport ? view.target : null);
    const [costKey, setCostKey] = useState<CostKey | null>(null);
    const brandStyle = brandingStyle(report.tenant);

    const costsShown = report.costs.visible && report.costs.blocks.length > 0;
    const nhgChapter =
        fullReport && costsShown
            ? (fullReport.chapters.find((item): item is NhgChapter => item.kind === 'nhg') ?? null)
            : null;
    const tabs = useMemo(
        () =>
            (['overzicht', 'kosten', 'rapport', 'nhg'] as const).filter(
                (item) =>
                    (item !== 'kosten' || costsShown) && (item !== 'nhg' || nhgChapter !== null),
            ),
        [costsShown, nhgChapter],
    );
    const tab: ReportTab = fullReport && tabs.includes(view.tab) ? view.tab : 'overzicht';

    /* Hoeveel bevindingen er achter elk bedrag staan; zonder volledig rapport geen. */
    const costFindings = useMemo(
        () =>
            Object.fromEntries(
                report.costs.blocks.map((block) => [
                    block.key,
                    costBreakdown(reading.index, block.key, block.amount).rows.length,
                ]),
            ) as Partial<Record<CostKey, number>>,
        [report.costs.blocks, reading.index],
    );

    const blocksAfterOverview = (
        <>
            {report.energyLabel ? (
                <EnergyLabelBlock label={report.energyLabel} language={language} copy={copy} />
            ) : null}

            {report.inspector ? (
                <InspectorCard
                    inspector={report.inspector}
                    copy={copy}
                    headingLevel={sectionLevel}
                    onAsk={onAsk}
                    {...(previewNote ? { previewNote } : {})}
                />
            ) : null}

            <ShareBlock
                sharing={report.sharing}
                language={language}
                copy={copy}
                headingLevel={sectionLevel}
                onShare={onShare}
                {...(previewNote ? { previewNote } : {})}
            />

            {report.invoice ? (
                <InvoiceBlock invoice={report.invoice} language={language} copy={copy} />
            ) : null}
        </>
    );

    return (
        <>
            <CompanyBar
                tenant={report.tenant}
                language={language}
                onLanguageChange={onLanguageChange}
                copy={copy}
            />

            <TranslationNotice
                contentLanguage={report.language}
                state={translationState}
                copy={copy}
            />

            {fullReport ? (
                <ReportTabbed
                    report={report}
                    fullReport={fullReport}
                    reading={reading}
                    tabs={tabs}
                    tab={tab}
                    view={view}
                    nhgChapter={nhgChapter}
                    language={language}
                    copy={copy}
                    heroLevel={heroLevel}
                    idPrefix={idPrefix}
                    brandStyle={brandStyle}
                    costFindings={costFindings}
                    onShowCosts={setCostKey}
                    afterOverview={blocksAfterOverview}
                    pinned={pinned}
                    {...(onOpenDocument ? { onOpenDocument } : {})}
                    {...(documentState ? { documentState } : {})}
                    {...(onLinkExpired ? { onLinkExpired } : {})}
                    costKey={costKey}
                    onCloseCosts={() => setCostKey(null)}
                />
            ) : (
                <>
                    <ReportHero
                        service={report.service}
                        address={report.address}
                        summary={report.summary}
                        publishedAt={report.publishedAt}
                        validUntil={report.validUntil}
                        documents={report.documents}
                        language={language}
                        copy={copy}
                        headingLevel={heroLevel}
                        {...(onOpenDocument ? { onOpenDocument } : {})}
                        {...(documentState ? { documentState } : {})}
                    />

                    <CostBlocks
                        costs={report.costs}
                        language={language}
                        copy={copy}
                        headingLevel={sectionLevel}
                    />

                    <KeyFindings
                        findings={report.findings}
                        showCosts={report.costs.visible}
                        language={language}
                        copy={copy}
                        headingLevel={sectionLevel}
                    />

                    <NhgBlock
                        nhg={report.nhg}
                        language={language}
                        copy={copy}
                        headingLevel={sectionLevel}
                    />

                    <PartsList
                        parts={report.parts}
                        note={report.partsNote}
                        copy={copy}
                        headingLevel={sectionLevel}
                    />

                    {blocksAfterOverview}
                </>
            )}

            {/*
             * Onder een opengeklapt hoofdstuk Voorwaarden staan de voorwaarden al voluit;
             * de voet herhaalt ze dan niet. Dichtgeklapt, of weggefilterd, zegt de voet
             * ze, zoals altijd.
             */}
            <PublicFooter
                footer={report.footer}
                withDisclaimer={
                    tab !== 'rapport' ||
                    !reading.isOpen('voorwaarden') ||
                    reading.filter !== 'alles'
                }
            />
        </>
    );
}

/**
 * De tabbladen en wat eronder staat, met alles wat tussen de tabbladen heen en
 * weer gaat: een sprong van een regel naar het rapport, van een bevinding naar
 * de NHG-bijlage en terug, en weer terug naar de regel waar de koper vandaan kwam.
 */
function ReportTabbed({
    report,
    fullReport,
    reading,
    tabs,
    tab,
    view,
    nhgChapter,
    language,
    copy,
    heroLevel,
    idPrefix,
    brandStyle,
    costFindings,
    onShowCosts,
    afterOverview,
    onOpenDocument,
    documentState,
    onLinkExpired,
    costKey,
    onCloseCosts,
    pinned,
}: {
    report: PublicReport;
    fullReport: PublicFullReport;
    reading: FullReportState;
    tabs: readonly ReportTab[];
    tab: ReportTab;
    view: ReportView;
    nhgChapter: NhgChapter | null;
    language: PublicLanguage;
    copy: PublicCopy;
    heroLevel: 1 | 2;
    idPrefix: string;
    brandStyle: CSSProperties;
    costFindings: Partial<Record<CostKey, number>>;
    onShowCosts: (key: CostKey) => void;
    afterOverview: ReactNode;
    onOpenDocument?: (document: PublicDocument) => boolean | void;
    documentState?: PublicDocumentState;
    onLinkExpired?: () => void;
    costKey: CostKey | null;
    onCloseCosts: () => void;
    /** Blijven de tabbladen en de leesbalk bovenaan plakken? In het kader van kantoor niet. */
    pinned: boolean;
}) {
    const sectionLevel = heroLevel === 1 ? 2 : 3;
    const outline = useMemo(() => reportOutline(fullReport), [fullReport]);
    const costs = useMemo(() => costTable(fullReport), [fullReport]);
    /* De oordelen en hun kleur, boven het overzicht: vóór het eerste oordeel. */
    const guide = useMemo(() => {
        const chapter = fullReport.chapters.find(
            (item): item is GuideChapter => item.kind === 'guide',
        );

        return chapter ? { key: chapter.key, judgements: chapter.judgements.items } : null;
    }, [fullReport]);
    const panelRef = useRef<HTMLDivElement | null>(null);
    const [nhgTarget, setNhgTarget] = useState<{ anchor: string; serial: number } | null>(null);

    /* De regel waar de koper het laatst op tikte, om daar weer uit te komen. */
    const lastRow = useRef<{ tab: ListTab; key: string } | null>(null);
    /* Het hoofdstuk dat de koper in het rapport leest. */
    const activeChapter = useRef<string | null>(null);
    /* Wat een tabblad doet als de pagina er zelf heen gaat; zonder: Terug of Vooruit. */
    const arrival = useRef<Arrival | null>(null);

    /**
     * Net voor het rapport dicht gaat: de huidige stap in de geschiedenis wijst
     * naar de plek waar de koper las, zodat Terug daar weer uitkomt. Samen met de
     * stap naar het andere tabblad één verandering, dus zonder sprong.
     */
    const keepReadingPlace = (key: string | null = activeChapter.current) => {
        if (tab === 'rapport' && key !== null) {
            view.showTarget(key);
        }
    };

    const selectTab = (next: ReportTab) => {
        keepReadingPlace();
        arrival.current = { kind: 'top' };
        view.selectTab(next);
    };

    /** Van een regel van het overzicht of de kosten naar die plek in het rapport. */
    const jumpFromList = (from: ListTab, key: string) => {
        if (!reading.revealTarget(key, { navigating: true })) {
            return;
        }

        lastRow.current = { tab: from, key };
        arrival.current = { kind: 'self' };
        view.showTarget(key, { push: true });
    };

    /* Van een bedrag, of een regel van de NHG-bijlage, naar de bevinding in het rapport. */
    const showEntry = (indexed: IndexedEntry) => {
        onCloseCosts();
        reading.revealEntry(indexed, { navigating: true });
        arrival.current = { kind: 'self' };
        view.showTarget(indexed.targetKey, { push: true });
    };

    /* Van de NHG-code onder een bevinding naar zijn regel in de bijlage; Terug komt weer bij die bevinding. */
    const showNhgRow = (code: string, from: IndexedEntry | null) => {
        keepReadingPlace(from?.targetKey ?? activeChapter.current);
        setNhgTarget((current) => ({
            anchor: nhgRowAnchor(code),
            serial: (current?.serial ?? 0) + 1,
        }));
        arrival.current = { kind: 'self' };
        view.selectTab('nhg');
    };

    /* Terug naar het overzicht: bij de regel waar de koper vandaan kwam, of die van het hoofdstuk in beeld. */
    const backToOverview = () => {
        const chapter = activeChapter.current;
        const row = lastRow.current;
        const key =
            row?.tab === 'overzicht' &&
            (chapter === null || reading.index.targets.get(row.key)?.chapterKey === chapter)
                ? row.key
                : chapter;

        keepReadingPlace();
        arrival.current = key === null ? { kind: 'top' } : { kind: 'row', id: outlineRowId(key) };
        view.selectTab('overzicht');
    };

    const onActiveChange = useCallback((key: string | null) => {
        activeChapter.current = key;
    }, []);

    const onNhgTargetDone = useCallback(() => setNhgTarget(null), []);

    /* Een tabblad dat net open is: bovenaan, terug bij een regel, of zijn eigen sprong. */
    const previousTab = useRef(tab);
    const releaseRow = useRef<() => void>(() => undefined);

    useEffect(() => {
        const current = releaseRow;

        return () => current.current();
    }, []);

    useEffect(() => {
        if (previousTab.current === tab) {
            return;
        }

        previousTab.current = tab;

        const how = arrival.current ?? historyArrival(tab, view.target, lastRow.current);

        arrival.current = null;
        releaseRow.current();
        releaseRow.current = () => undefined;

        if (how.kind === 'row' && document.getElementById(how.id)) {
            releaseRow.current = jumpTo(how.id, { center: true });
        } else if (how.kind !== 'self') {
            toPanelTop(panelRef.current);
        }
    }, [tab, view.target]);

    return (
        <>
            <ReportTabs
                tabs={tabs}
                tab={tab}
                onSelect={selectTab}
                copy={copy}
                idPrefix={idPrefix}
                pinned={pinned}
            />

            <div
                ref={panelRef}
                role="tabpanel"
                id={reportPanelId(idPrefix)}
                aria-labelledby={reportTabId(idPrefix, tab)}
                className="scroll-mt-[49px]"
            >
                {tab === 'overzicht' ? (
                    <>
                        <ReportSummary
                            report={report}
                            cover={fullReport.cover}
                            index={reading.index}
                            language={language}
                            copy={copy}
                            headingLevel={heroLevel}
                            onShowCosts={onShowCosts}
                            {...(reading.index.targets.has('samenvatting')
                                ? { onReadSummary: () => jumpFromList('overzicht', 'samenvatting') }
                                : {})}
                            {...(onOpenDocument ? { onOpenDocument } : {})}
                            {...(documentState ? { documentState } : {})}
                        />

                        <ReportOutline
                            items={outline}
                            note={report.partsNote}
                            copy={copy}
                            headingLevel={sectionLevel}
                            onJump={(key) => jumpFromList('overzicht', key)}
                            guide={guide}
                        />

                        {afterOverview}
                    </>
                ) : null}

                {tab === 'kosten' ? (
                    <>
                        <CostBlocks
                            costs={report.costs}
                            language={language}
                            copy={copy}
                            headingLevel={heroLevel}
                            onShow={onShowCosts}
                            findingCounts={costFindings}
                        />

                        {costs ? (
                            <CostTable
                                table={costs}
                                language={language}
                                copy={copy}
                                headingLevel={sectionLevel}
                                onJump={(key) => jumpFromList('kosten', key)}
                            />
                        ) : null}
                    </>
                ) : null}

                {tab === 'rapport' ? (
                    <FullReport
                        report={report}
                        fullReport={fullReport}
                        state={reading}
                        language={language}
                        copy={copy}
                        headingLevel={heroLevel}
                        onTargetChange={view.showTarget}
                        onActiveChange={onActiveChange}
                        onBackToOverview={backToOverview}
                        onNhgJump={showNhgRow}
                        brandStyle={brandStyle}
                        pinned={pinned}
                        {...(onOpenDocument ? { onOpenDocument } : {})}
                        {...(documentState ? { documentState } : {})}
                        {...(onLinkExpired ? { onLinkExpired } : {})}
                    />
                ) : null}

                {tab === 'nhg' && nhgChapter ? (
                    <NhgTab
                        chapter={nhgChapter}
                        index={reading.index}
                        language={language}
                        copy={copy}
                        headingLevel={heroLevel}
                        target={nhgTarget}
                        onTargetDone={onNhgTargetDone}
                        onEntryJump={showEntry}
                    />
                ) : null}
            </div>

            <CostBreakdownDrawer
                costKey={costKey}
                costs={report.costs}
                index={reading.index}
                language={language}
                copy={copy}
                brandStyle={brandStyle}
                onClose={onCloseCosts}
                onShow={showEntry}
            />
        </>
    );
}

/**
 * Een tabblad dat opengaat door Terug of Vooruit in de browser: terug bij de
 * regel waar de koper vandaan kwam, of naar zijn plek in het rapport (dat
 * springt zelf), en anders bovenaan.
 */
function historyArrival(
    tab: ReportTab,
    target: string | null,
    lastRow: { tab: ListTab; key: string } | null,
): Arrival {
    if ((tab === 'overzicht' || tab === 'kosten') && lastRow?.tab === tab) {
        return {
            kind: 'row',
            id: tab === 'overzicht' ? outlineRowId(lastRow.key) : costRowId(lastRow.key),
        };
    }

    return tab === 'rapport' && target !== null ? { kind: 'self' } : { kind: 'top' };
}

/**
 * Een nieuw tabblad begint bovenaan, net onder de tabbladen - maar alleen als
 * de lezer al voorbij de tabbladen was; staan ze nog op hun plek, dan blijft de
 * pagina staan.
 */
function toPanelTop(panel: HTMLElement | null) {
    const tabs = panel?.parentElement?.querySelector('[role="tablist"]');

    if (!panel || !tabs) {
        return;
    }

    if (panel.getBoundingClientRect().top < tabs.getBoundingClientRect().bottom - 1) {
        panel.scrollIntoView?.({ block: 'start' });
    }
}
